import PropTypes from 'prop-types'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { FiChevronDown, FiSearch } from 'react-icons/fi'

import './style.scss'

export const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'closing', label: 'Closing soon' },
  { value: 'expired', label: 'Expired' },
]

const ToolbarMenu = ({ children, className = '' }) => {
  const menuRef = useRef(null)

  useLayoutEffect(() => {
    const adjustPosition = () => {
      const el = menuRef.current
      if (!el) return

      el.style.transform = ''
      const rect = el.getBoundingClientRect()
      const padding = 8

      if (rect.right > window.innerWidth - padding) {
        const offset = rect.right - (window.innerWidth - padding)
        el.style.transform = `translateX(-${offset}px)`
      } else if (rect.left < padding) {
        const offset = padding - rect.left
        el.style.transform = `translateX(${offset}px)`
      }
    }

    adjustPosition()
    window.addEventListener('resize', adjustPosition)
    return () => {
      window.removeEventListener('resize', adjustPosition)
    }
  }, [])

  return (
    <div ref={menuRef} className={`hosted-event-toolbar__menu ${className}`.trim()}>
      {children}
    </div>
  )
}

ToolbarMenu.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
}

const EventToolbar = ({
  searchValue,
  onSearchChange,
  filter,
  onFilterChange,
  sortOption,
  sortOptions,
  onSortChange,
  view,
  onViewChange,
}) => {
  const [openMenu, setOpenMenu] = useState(null)
  const toolbarRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (toolbarRef.current && !toolbarRef.current.contains(event.target)) {
        setOpenMenu(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const currentFilterLabel = FILTER_OPTIONS.find((opt) => opt.value === filter)?.label || 'Filter'
  const currentSortLabel = sortOptions.find((opt) => opt.value === sortOption)?.label || 'Sort By'

  const toggleMenu = (menu) => setOpenMenu((prev) => (prev === menu ? null : menu))

  return (
    <div className="hosted-event-toolbar" ref={toolbarRef}>
      <div className="hosted-event-toolbar__search">
        <FiSearch />
        <input
          type="text"
          placeholder="Search events…"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      <div className="hosted-event-toolbar__dropdown">
        <button
          type="button"
          className="btn secondary"
          id="filter-btn"
          onClick={() => toggleMenu('filter')}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="8" y1="12" x2="16" y2="12" />
            <line x1="11" y1="18" x2="13" y2="18" />
          </svg>
          {currentFilterLabel}
        </button>
        {openMenu === 'filter' && (
          <ToolbarMenu>
            {FILTER_OPTIONS.map((opt) => (
              <div
                key={opt.value}
                className={filter === opt.value ? 'is-active' : ''}
                onClick={() => {
                  onFilterChange(opt.value)
                  setOpenMenu(null)
                }}
              >
                {opt.label}
              </div>
            ))}
          </ToolbarMenu>
        )}
      </div>

      <div className="hosted-event-toolbar__dropdown">
        <button type="button" onClick={() => toggleMenu('sort')}>
          {currentSortLabel}
          <FiChevronDown size={13} />
        </button>
        {openMenu === 'sort' && (
          <ToolbarMenu>
            {sortOptions.map((opt) => (
              <div
                key={opt.value}
                className={sortOption === opt.value ? 'is-active' : ''}
                onClick={() => {
                  onSortChange(opt.value)
                  setOpenMenu(null)
                }}
              >
                {opt.label}
              </div>
            ))}
          </ToolbarMenu>
        )}
      </div>

      <div className="hosted-event-toolbar__view-toggle view-toggle" id="view-toggle">
        <button
          type="button"
          className={`btn ${view === 'tile' ? 'active' : ''}`}
          id="btn-tile"
          title="Tile view"
          onClick={() => onViewChange('tile')}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
            <rect x="0" y="0" width="6" height="6" rx="1" />
            <rect x="8" y="0" width="6" height="6" rx="1" />
            <rect x="0" y="8" width="6" height="6" rx="1" />
            <rect x="8" y="8" width="6" height="6" rx="1" />
          </svg>
        </button>
        <button
          type="button"
          className={`btn ${view === 'list' ? 'active' : ''}`}
          id="btn-list"
          title="List view"
          onClick={() => onViewChange('list')}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
            <rect y="1" width="14" height="2" rx="1" />
            <rect y="6" width="14" height="2" rx="1" />
            <rect y="11" width="14" height="2" rx="1" />
          </svg>
        </button>
      </div>
    </div>
  )
}

EventToolbar.propTypes = {
  searchValue: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  filter: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  sortOption: PropTypes.string.isRequired,
  sortOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  onSortChange: PropTypes.func.isRequired,
  view: PropTypes.oneOf(['tile', 'list']).isRequired,
  onViewChange: PropTypes.func.isRequired,
}

export default EventToolbar
